import React, { useEffect, useRef, useState } from "react";
import { lightTheme, darkTheme } from "./theme";
import {
  BrowserRouter as Router,
  Navigate,
  Route,
  Routes,
} from "react-router-dom";
import Home from "./components/home/Home";
import Contact from "./components/Contact";
import About from "./components/About";
import Services from "./components/Services";
import Resources from "./components/Resources";
import Pricing from "./components/Pricing";
import ProductPage from "./components/ProductPage";
import ProductSamplePage from "./components/ProductSamplePage";
import CookieConsent from "./components/CookieConsent";
import LoginPage from "./components/login/LoginPage";
import CreateAccountOptions from "./createAccount/createaccountoptions/CreateAccountOptions";
import CreateAccountForm from "./createAccount/createaccountform/CreateAccountForm";
import ToggleDrawer from "./components/ToggleDrawer";
import Appbar from "./components/Appbar";
import MainPage from "./components/mainpage/MainPage";
import LessonPage from "./components/lessons/LessonPage";
import CoursePage from "./components/courses/ViewCoursePage";
import SignIn from "./components/Signin";
import QuizPage from "./components/quiz/QuizPage";
import Assignment from "./components/assignments/Assignment";
import Enroll from "./components/Enroll";
import TasksAndBookmarksPage from "./components/assignments/TasksAndBookmarksPage";
import AssignmentsToGrade from "./components/assignments/AssignmentsToGrade";
import SearchResultsPage from "./components/mainpage/SearchResultsPage";
import ProtectedRoute from "./components/ProtectedRoute";
import AuthoringLayout from "./components/authoring/AuthoringLayout";
import AuthoringWorkspace from "./components/authoring/AuthoringWorkspace";
import ItemDetailRouter from "./components/authoring/items/ItemDetailRouter";
import OpenGameroom from "./components/services/OpenGameroom";
import WatchGame from "./components/services/WatchGame";
import JoinGame from "./components/services/JoinGame";
import Activity from "./components/services/Activity";
import Authorized from "./components/Authorized";
import Gameroom from "./components/services/Gameroom";
import { ensureLessonFontStyles } from "./utils/fontFamilies";

import {
  ThemeProvider,
  CssBaseline,
  Drawer,
  useMediaQuery,
} from "@mui/material";

const drawerWidth = 240;
const App = () => {
  const [themeMode, setThemeMode] = useState("light");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [showAppBar, setShowAppBar] = useState(true); // AppBar visibility state
  const isMobile = useMediaQuery("(max-width:825px)");
  const aboutRef = useRef(null);
  const instRef = useRef(null);
  const testimonialRef = useRef(null);
  const compareProRef = useRef(null);
  const QARef = useRef(null);
  const [assignmentDueCount, setAssignmentDueCounts] = useState(null);

  useEffect(() => {
    ensureLessonFontStyles();
  }, []);

  const scrollToSection = (ref) => {
    ref.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleLoginSuccess = (data) => {
    setUserInfo(data);
  };
  const handleLogout = () => {
    setUserInfo(null);
    window.location.href = "/"; // Redirect to home page
  };

  // const ProtectedRoute = ({ children }) => {
  //   if (!userInfo) {
  //     // Redirect the user to the Google login page if they're not authenticated
  //     window.location.href =
  //       "https://accounts.google.com/o/oauth2/v2/auth/oauthchooseaccount?client_id=106972638172-0hmocdv6qc4755jn5sgf6dm97e45j10u.apps.googleusercontent.com&redirect_uri=http%3A%2F%2Flocalhost%3A3000&response_type=token&scope=profile%20email&service=lso&o2v=2&ddm=1&flowName=GeneralOAuthFlow";
  //     return null; // We return null because we're redirecting
  //   }
  //   return children;
  // };
  const toggleTheme = () => {
    setThemeMode((prev) => (prev === "light" ? "dark" : "light"));
  };
  const handleDrawerToggle = () => {
    setMobileOpen((prevState) => !prevState);
  };

  return (
    <ThemeProvider theme={themeMode === "light" ? lightTheme : darkTheme}>
      <CssBaseline />
      <CookieConsent />
      <Router>
        {showAppBar && (
          <Appbar
            isMobile={isMobile}
            handleDrawerToggle={handleDrawerToggle}
            handleLogout={handleLogout}
            userInfo={userInfo}
            handleLoginSuccess={handleLoginSuccess}
            themeMode={themeMode}
            toggleTheme={toggleTheme}
            assignmentDueCount={assignmentDueCount}
            onAboutClick={() => {
              scrollToSection(aboutRef);
            }}
            onInstClick={() => {
              scrollToSection(instRef);
            }}
            ontestimonialClick={() => {
              scrollToSection(testimonialRef);
            }}
            onCompareProClick={() => {
              scrollToSection(compareProRef);
            }}
            onQARefClick={() => {
              scrollToSection(QARef);
            }}
          />
        )}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: "block", sm: "block" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
            },
          }}
        >
          <ToggleDrawer
            handleDrawerToggle={handleDrawerToggle}
            mobileOpen={mobileOpen}
            onAboutClick={() => {
              scrollToSection(aboutRef);
            }}
            onInstClick={() => {
              scrollToSection(instRef);
            }}
            ontestimonialClick={() => {
              scrollToSection(testimonialRef);
            }}
            onCompareProClick={() => {
              scrollToSection(compareProRef);
            }}
            onQARefClick={() => {
              scrollToSection(QARef);
            }}
          />
        </Drawer>

        <Routes>
          <Route
            path="/"
            element={
              <Home
                aboutRef={aboutRef}
                instRef={instRef}
                testimonialRef={testimonialRef}
                compareProRef={compareProRef}
                QARef={QARef}
              />
            }
          />
          <Route path="/enroll" element={<Enroll />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/resources" element={<Resources />} />
          <Route path="/services" element={<Services />} />
          <Route path="/productsamplepage" element={<ProductSamplePage />} />
          <Route path="Signin" element={<SignIn />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/product/:id" element={<ProductPage />} />
          <Route
            path="/loginpage"
            element={<LoginPage setShowAppBar={setShowAppBar} />}
          />
          <Route
            path="/createaccountoptions"
            element={<CreateAccountOptions setShowAppBar={setShowAppBar} />}
          />
          <Route
            path="/Createaccountform"
            element={<CreateAccountForm setShowAppBar={setShowAppBar} />}
          />
          <Route path="/assignmentsdue" element={<TasksAndBookmarksPage />} />
          <Route
            path="/bookmarkedlessons"
            element={<TasksAndBookmarksPage />}
          />
          <Route
            path="/Grade/course/:courseId"
            element={<AssignmentsToGrade />}
          />
          <Route element={<ProtectedRoute />}>
            <Route
              path="/coursepage"
              element={
                <CoursePage
                  setShowAppBar={setShowAppBar}
                  setAssignmentDueCounts={setAssignmentDueCounts}
                />
              }
            />

            <Route path="/authoring" element={<AuthoringLayout />}>
              <Route index element={<Navigate to="items/mcma" replace />} />
              <Route path=":section/:type" element={<AuthoringWorkspace />}>
                <Route path="item/:itemId" element={<ItemDetailRouter />} />
              </Route>
            </Route>

            {/* <Route path="/Modules/Course" element={<CoursePage />} /> */}

            <Route
              path="/Modules/Course/:courseId"
              element={<MainPage setShowAppBar={setShowAppBar} />}
            >
              {/* Nested route should be relative */}
              <Route
                path="searchresultspage"
                element={<SearchResultsPage />}
              ></Route>
              <Route
                path="Module/:moduleId/Chapter/:chapterId/Lesson/:lessonId"
                element={<LessonPage />}
              >
                <Route path="quizpage" element={<QuizPage />} />
                <Route path="assignment" element={<Assignment />} />
              </Route>
            </Route>
            <Route
              path="/OpenGameroom/course/:courseId/module/:moduleId/chapter/:chapterId/game/:contentId"
              element={<OpenGameroom setShowAppBar={setShowAppBar} />}
            />
            <Route
              path="/WatchGame/:gameCode"
              element={<WatchGame setShowAppBar={setShowAppBar} />}
            />
            <Route
              path="/Game/:gameCode/course/:courseId/module/:moduleId/chapter/:chapterId/content/:lessonId"
              element={<Activity setShowAppBar={setShowAppBar} />}
            />
          </Route>
          <Route
            path="/Gameroom"
            element={
              <Authorized refRoute="Gameroom">
                <Gameroom setShowAppBar={setShowAppBar} />
              </Authorized>
            }
          />
        </Routes>
      </Router>
    </ThemeProvider>
  );
};

export default App;
